import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import logo from "./images/techvanto-logo.png";

const LOGO_SRC = logo;

// ── Nav Data ──────────────────────────────────────────────────────────────────
const NAV_LINKS = [
  {
    label: "Services",
    dropdown: [
      { name: "Web Development", link: "/web-development" },
      { name: "Mobile App Development", link: "/mobile-app-development" },
      { name: "AI & ML Solutions", link: "/ai-ml-solutions" },
      { name: "Cloud DevOps", link: "/cloud" },
      { name: "UI/UX Design", link: "/ui-ux" },
      { name: "E-Commerce Solutions", link: "/ecommerce" },
    ],
  },
  {
    label: "Solutions",
    dropdown: [
      { name: "CRM Solution", link: "/crm-solutions" },
      { name: "ERP Solution", link: "/erp-solutions" },
      { name: "HRM Solution", link: "/hrm-solutions" },
      { name: "LMS Solution", link: "/lms-solutions" },
      { name: "POS Solution", link: "/pos-solutions" },
      { name: "SaaS Platform", link: "/saas-platform" },
    ],
  },
  {
    label: "Industries",
      dropdown: [
      { name: "Healthcare", link: "/healthcare-solution" },
      { name: "FinTech", link: "/fintech-solution" },
      { name: "EdTech", link: "/edtech-solution" },
      { name: "Retail & E-Commerce", link: "retail-ecommerce-solution" },
      { name: "Real Estate", link: "/realestate-solution" },
      { name: "Logistics", link: "/logistic-solution" },
    ],
  },
  { label: "Portfolio", dropdown: null, link: "/portfolio" },
  { label: "About", dropdown: null, link: "/#about" },
  { label: "Blog", dropdown: null, link: "/blogs" },
];
// ── Page Data ─────────────────────────────────────────────────────────────────

const SERVICES = [
  {
    icon: "🔍",
    title: "UX Research & Strategy",
    desc: "User interviews, heuristic audits, competitive analysis, journey mapping, and persona creation — turning real human behaviour data into a bulletproof design strategy before a single pixel is placed.",
    tags: ["User Interviews", "Journey Mapping", "Heuristic Audit", "Personas"],
    color: "#F43F5E",
  },
  {
    icon: "🖼️",
    title: "Wireframing & Prototyping",
    desc: "From low-fidelity sketches to high-fidelity interactive Figma prototypes — we validate flows and interactions with real users before development begins, eliminating costly late-stage pivots.",
    tags: ["Figma", "Low-Fi", "High-Fi", "Clickable Prototypes"],
    color: "#8B5CF6",
  },
  {
    icon: "🎨",
    title: "Visual & Brand Design",
    desc: "Design systems, typography scales, colour palettes, icon libraries, and brand guidelines that ensure pixel-perfect consistency across every touchpoint — web, mobile, and print.",
    tags: ["Design Systems", "Brand Identity", "Typography", "Colour Theory"],
    color: "#F59E0B",
  },
  {
    icon: "📱",
    title: "Mobile App UI Design",
    desc: "Native iOS and Android interfaces that follow platform HIG guidelines while maintaining your brand personality — optimised for thumb zones, accessibility, and delight at every tap.",
    tags: ["iOS HIG", "Material Design", "Figma", "Accessibility"],
    color: "#10B981",
  },
  {
    icon: "🖥️",
    title: "Web & SaaS Dashboard Design",
    desc: "Complex data-heavy dashboards, admin panels, and SaaS product interfaces designed for clarity and speed — information architecture that lets power users fly and new users onboard instantly.",
    tags: ["Dashboard UI", "Data Visualisation", "IA", "Figma"],
    color: "#3B82F6",
  },
  {
    icon: "🧪",
    title: "Usability Testing",
    desc: "Moderated and unmoderated user testing sessions, A/B test design, heatmap analysis, and session recording review — evidence-based iteration that removes friction and increases conversions.",
    tags: ["Hotjar", "Maze", "A/B Testing", "Session Replay"],
    color: "#EC4899",
  },
  {
    icon: "♿",
    title: "Accessibility & WCAG Compliance",
    desc: "WCAG 2.2 AA/AAA audits, colour contrast corrections, screen-reader-friendly markup, keyboard navigation, and inclusive design patterns — making your product usable by everyone.",
    tags: ["WCAG 2.2", "ARIA", "Colour Contrast", "Screen Reader"],
    color: "#06B6D4",
  },
  {
    icon: "🔄",
    title: "Design-to-Dev Handoff",
    desc: "Developer-ready Figma files with auto-layout, component variants, token documentation, and annotated specs — so your engineering team ships pixel-perfect UI without back-and-forth.",
    tags: ["Figma Tokens", "Storybook", "Dev Mode", "Zeplin"],
    color: "#6366F1",
  },
];

const TECH_STACK = [
  {
    category: "Design Tools",
    items: [
      { name: "Figma", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/figma/figma-original.svg", color: "#F24E1E" },
      { name: "Adobe XD", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/xd/xd-plain.svg", color: "#FF61F6" },
      { name: "Sketch", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/sketch/sketch-original.svg", color: "#F7B500" },
      { name: "Illustrator", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/illustrator/illustrator-plain.svg", color: "#FF9A00" },
      { name: "Photoshop", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/photoshop/photoshop-plain.svg", color: "#31A8FF" },
      { name: "After Effects", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/aftereffects/aftereffects-plain.svg", color: "#9999FF" },
    ],
  },
  {
    category: "Prototyping",
    items: [
      { name: "Figma Prototyping", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/figma/figma-original.svg", color: "#F24E1E" },
      { name: "Framer", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg", color: "#0055FF" },
      { name: "InVision", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/figma/figma-original.svg", color: "#FF3366" },
      { name: "Maze", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#FF5C35" },
      { name: "ProtoPie", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/figma/figma-original.svg", color: "#FF3D88" },
      { name: "Lottie", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/javascript/javascript-original.svg", color: "#00C4B4" },
    ],
  },
  {
    category: "Research & Testing",
    items: [
      { name: "Hotjar", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#FF3C00" },
      { name: "Mixpanel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#7856FF" },
      { name: "Google Analytics", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/google/google-original.svg", color: "#E37400" },
      { name: "Miro", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/figma/figma-original.svg", color: "#FFD02F" },
      { name: "Notion", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/notion/notion-original.svg", color: "#000000" },
      { name: "Optimal Workshop", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#FF6B35" },
    ],
  },
  {
    category: "Dev Handoff",
    items: [
      { name: "Figma Dev Mode", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/figma/figma-original.svg", color: "#F24E1E" },
      { name: "Storybook", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/storybook/storybook-original.svg", color: "#FF4785" },
      { name: "Zeplin", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/figma/figma-original.svg", color: "#FDBD39" },
      { name: "React", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg", color: "#61DAFB" },
      { name: "Tailwind CSS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tailwindcss/tailwindcss-original.svg", color: "#06B6D4" },
      { name: "CSS / SCSS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/css3/css3-original.svg", color: "#1572B6" },
    ],
  },
];

const PROCESS = [
  { n: "01", icon: "🔍", title: "Discover & Research", desc: "Stakeholder workshops, user interviews, analytics deep-dives, and competitive audits. We build empathy with real users and map every pain point before ideating solutions.", color: "#F43F5E" },
  { n: "02", icon: "🗺️", title: "Define & Strategise", desc: "Information architecture, user flows, sitemap design, and problem statements. We align design goals with business KPIs — so every decision has a measurable outcome.", color: "#8B5CF6" },
  { n: "03", icon: "✏️", title: "Ideate & Wireframe", desc: "Rapid sketching, lo-fi wireframes, and interaction flows validated with real users in days — not weeks. We kill bad ideas cheaply, before they reach Figma.", color: "#F59E0B" },
  { n: "04", icon: "🎨", title: "Design & Prototype", desc: "High-fidelity UI design and interactive Figma prototypes — complete with micro-animations, state management, design tokens, and a full component library.", color: "#10B981" },
  { n: "05", icon: "🧪", title: "Test & Iterate", desc: "Moderated usability testing, A/B experiments, accessibility audits, and heatmap analysis — evidence-based refinement until every interaction feels inevitable.", color: "#3B82F6" },
  { n: "06", icon: "🚀", title: "Handoff & Support", desc: "Developer-ready Figma files with annotated specs, design tokens, Storybook documentation, and ongoing design support throughout the development sprint cycle.", color: "#EC4899" },
];

const CASE_STUDIES = [
  {
    title: "ZenPay — FinTech Super App",
    industry: "FinTech",
    desc: "Complete UX overhaul of a personal finance app serving 800K users — reducing onboarding drop-off by 61% and increasing daily active usage by 3.2× through a redesigned information architecture and trust-first visual language.",
    img: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600&h=400&fit=crop&auto=format",
    tags: ["Figma", "UX Research", "Design System", "Usability Testing"],
    metrics: [{ val: "61%", lbl: "Drop-off Reduced" }, { val: "3.2×", lbl: "Daily Active Users" }, { val: "4.9★", lbl: "App Store Rating" }],
    color: "#F43F5E",
  },
  {
    title: "Curately — SaaS Dashboard",
    industry: "SaaS",
    desc: "Designed the entire product UI for a content intelligence SaaS from zero — including a complex analytics dashboard, multi-step onboarding, and a living design system with 200+ components adopted by the engineering team.",
    img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop&auto=format",
    tags: ["Dashboard UI", "Design System", "Figma", "Storybook"],
    metrics: [{ val: "200+", lbl: "Components Built" }, { val: "40%", lbl: "Faster Dev Velocity" }, { val: "92", lbl: "NPS Score" }],
    color: "#8B5CF6",
  },
  {
    title: "HealTrack — Healthcare App",
    industry: "Healthcare",
    desc: "Redesigned a hospital patient management mobile app with a focus on accessibility (WCAG 2.2 AA) and clarity for elderly users — achieving a 78% task completion rate improvement and full compliance certification.",
    img: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&h=400&fit=crop&auto=format",
    tags: ["Mobile UI", "Accessibility", "WCAG 2.2", "User Testing"],
    metrics: [{ val: "78%", lbl: "Task Completion ↑" }, { val: "WCAG AA", lbl: "Certified" }, { val: "4.8★", lbl: "Play Store Rating" }],
    color: "#10B981",
  },
];

const FAQS = [
  {
    q: "What is your typical UI/UX design process and how long does it take?",
    a: "Our process has six stages: Discover, Define, Ideate, Design, Test, and Handoff. A focused landing page or marketing site redesign takes 2–4 weeks. A full mobile app or SaaS product design (including research and usability testing) takes 6–14 weeks. We always start with a scoped discovery sprint so you know exactly what you're getting before full engagement begins.",
  },
  {
    q: "Do you deliver design systems or just individual screens?",
    a: "We always deliver a full design system, not just screens. This includes a component library in Figma with auto-layout, variants, and interactive states; a token file (colours, typography, spacing, shadows); usage guidelines; and an accessibility annotation layer. If you use Storybook, we can document components there too. A design system is the most valuable long-term asset we create — it's the single source of truth for your product's visual identity.",
  },
  {
    q: "How do you ensure the designs are actually implementable by developers?",
    a: "Dev-readiness is part of our definition of 'done'. All Figma files use auto-layout so every component responds correctly to content changes. We use Figma Variables (tokens) mapped to CSS custom properties. Final files go through a handoff review checklist — every component has annotated specs, every interaction has a description, and edge cases are documented. We remain available for design QA throughout the development sprint.",
  },
  {
    q: "Can you work with our existing brand guidelines?",
    a: "Absolutely. We start by auditing your existing brand assets, style guides, and any existing UI components. We'll extend your brand system into a complete, consistent digital design language while flagging any areas where the current guidelines create UX or accessibility problems. We work within your brand, not despite it.",
  },
  {
    q: "Do you conduct real user research or is it assumption-based design?",
    a: "All our design decisions are grounded in evidence. Depending on scope, we conduct user interviews, card sorting, tree testing, first-click tests, and moderated usability sessions. For products with existing traffic, we layer in analytics and heatmap data. We document every insight and make every design decision traceable to a user need or business goal.",
  },
  {
    q: "What happens after the design is handed off?",
    a: "We offer ongoing design support retainers — joining your sprint planning, reviewing implemented UI against designs, iterating based on post-launch data, and expanding the design system as new features are built. Most clients keep us engaged throughout development because the design QA phase catches implementation issues early, when they're cheap to fix.",
  },
];

const STATS = [
  { val: "200+", lbl: "Products Designed", icon: "🎨" },
  { val: "4.9★", lbl: "Avg Client Rating", icon: "⭐" },
  { val: "61%", lbl: "Avg Drop-off Reduction", icon: "📈" },
  { val: "48h", lbl: "Discovery Kickoff", icon: "⚡" },
];

const USE_CASES = [
  { industry: "FinTech", icon: "💳", examples: ["Trust-first onboarding flows", "Transaction dashboard UI", "KYC/verification UX"], color: "#F43F5E" },
  { industry: "Healthcare", icon: "🏥", examples: ["Accessible patient apps", "Clinical dashboard design", "Telemedicine UX"], color: "#10B981" },
  { industry: "EdTech", icon: "🎓", examples: ["Adaptive LMS interface", "Student progress dashboards", "Gamified learning UI"], color: "#F59E0B" },
  { industry: "SaaS", icon: "💻", examples: ["Complex dashboard design", "Onboarding flow design", "Design system creation"], color: "#8B5CF6" },
  { industry: "E-Commerce", icon: "🛍️", examples: ["Conversion-optimised PDPs", "Checkout flow redesign", "Mobile-first shopping UX"], color: "#3B82F6" },
  { industry: "Logistics", icon: "🚚", examples: ["Fleet tracking UI", "Driver app design", "Operations dashboard"], color: "#EC4899" },
];

// ── Navbar ────────────────────────────────────────────────────────────────────
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Smart logic to check which page we are currently on
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
      background: scrolled ? "rgba(255,255,255,0.97)" : "rgba(255,255,255,0.9)",
      backdropFilter: "blur(16px)",
      borderBottom: scrolled ? "1px solid #E5E7EB" : "1px solid transparent",
      boxShadow: scrolled ? "0 2px 24px rgba(0,0,0,0.08)" : "none",
      transition: "all .3s ease",
    }}>
      <div style={{
        background: "linear-gradient(90deg,#1E3A8A,#3B82F6)",
        color: "#fff", fontSize: 12, padding: "6px 40px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexWrap: "wrap", gap: 8,
      }}>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          <span>📞 +91 96462 06032</span>
          <span>✉️ info@techvanto.com</span>
        </div>
        <span className="topbar-right" style={{ opacity: .85 }}>🌏 Trusted by 1500+ clients across 20+ countries</span>
      </div>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 40px", height: 68, maxWidth: 1280, margin: "0 auto",
      }}>
        <a href="/" style={{ display: "flex", alignItems: "center", flexShrink: 0, textDecoration: "none" }}>
          <img src={LOGO_SRC} alt="Techvanto" style={{ height: 150, width: "auto", objectFit: "contain", display: "block" }} />
        </a>
        <div className="nav-links" style={{ alignItems: "center", gap: 4 }}>
          {NAV_LINKS.map(link => {
            // Check if this specific main link is the active page
            const isActive = link.link && currentPath.includes(link.label.toLowerCase());

            return (
              <div key={link.label} style={{ position: "relative" }}
                onMouseEnter={() => link.dropdown && setActiveDropdown(link.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                {link.link ? (
                  <a href={link.link} style={{
                    background: isActive ? "#EFF6FF" : "none",
                    border: isActive ? "1px solid #BFDBFE" : "1px solid transparent",
                    cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                    color: isActive ? "#3B82F6" : "#374151",
                    display: "flex", alignItems: "center", gap: 4, transition: "color .2s, background .2s",
                    fontFamily: "'Sora',sans-serif", borderRadius: 8, textDecoration: "none"
                  }}>
                    {link.label}
                  </a>
                ) : (
                  <button style={{
                    // Highlight the "Services" button if we are on the web-development page
                    background: (link.label === "Services" && currentPath === "/web-development") ? "#EFF6FF" : "none",
                    border: (link.label === "Services" && currentPath === "/web-development") ? "1px solid #BFDBFE" : "1px solid transparent",
                    cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                    color: (link.label === "Services" && currentPath === "/web-development") ? "#3B82F6" : activeDropdown === link.label ? "#3B82F6" : "#374151",
                    display: "flex", alignItems: "center", gap: 4, transition: "color .2s",
                    fontFamily: "'Sora',sans-serif", borderRadius: 8,
                  }}>
                    {link.label}
                    {link.dropdown && <span style={{ fontSize: 10, transition: "transform .2s", transform: activeDropdown === link.label ? "rotate(180deg)" : "rotate(0)" }}>▾</span>}
                  </button>
                )}
                {link.dropdown && activeDropdown === link.label && (
                  <div style={{
                    position: "absolute", top: "100%", left: 0, zIndex: 200,
                    background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12,
                    boxShadow: "0 16px 48px rgba(0,0,0,0.12)", minWidth: 220,
                    padding: "8px 0", animation: "fadeIn .2s ease",
                  }}>
                    {link.dropdown.map(item => (
                      <a key={item.name} href={item.link} style={{
                        display: "block", padding: "9px 20px", fontSize: 13.5,
                        // Highlight "Web Development" inside the dropdown specifically
                        color: currentPath === item.link ? "#3B82F6" : "#374151",
                        background: currentPath === item.link ? "#EFF6FF" : "none",
                        fontWeight: currentPath === item.link ? 700 : 500,
                        textDecoration: "none", transition: "all .15s",
                      }}
                        onMouseEnter={e => { e.target.style.background = "#EFF6FF"; e.target.style.color = "#3B82F6"; }}
                        onMouseLeave={e => { 
                          e.target.style.background = currentPath === item.link ? "#EFF6FF" : "none"; 
                          e.target.style.color = currentPath === item.link ? "#3B82F6" : "#374151"; 
                        }}
                      >{item.name}</a>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <a href="/#contact" className="nav-cta" style={{
            background: "linear-gradient(135deg,#1E3A8A,#3B82F6)", color: "#fff",
            padding: "10px 22px", borderRadius: 8, fontSize: 13.5, fontWeight: 700,
            textDecoration: "none", boxShadow: "0 4px 14px rgba(59,130,246,.35)",
            transition: "transform .2s, box-shadow .2s", fontFamily: "'Sora',sans-serif",
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(59,130,246,.45)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(59,130,246,.35)"; }}
          >Get Free Consultation</a>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="mob-menu-btn" style={{
            background: "none", border: "1px solid #E5E7EB", borderRadius: 8,
            padding: "8px 12px", cursor: "pointer", fontSize: 18, color: "#374151",
          }}>☰</button>
        </div>
      </div>
      {mobileOpen && (
        <div style={{ background: "#fff", borderTop: "1px solid #E5E7EB", padding: "16px 24px 24px", display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ paddingBottom: 14, borderBottom: "1px solid #F3F4F6", marginBottom: 4 }}>
            <img src={LOGO_SRC} alt="Techvanto" style={{ height: 40, width: "auto", objectFit: "contain" }} />
          </div>
          {NAV_LINKS.map(l => (
            <a key={l.label} href={l.link || "#"} style={{ padding: "10px 0", color: currentPath.includes(l.label.toLowerCase()) ? "#3B82F6" : "#374151", fontWeight: 600, textDecoration: "none", borderBottom: "1px solid #F3F4F6", fontFamily: "'Sora',sans-serif" }}>{l.label}</a>
          ))}
          <a href="/#contact" style={{ marginTop: 12, background: "linear-gradient(135deg,#1E3A8A,#3B82F6)", color: "#fff", padding: "13px", borderRadius: 8, textAlign: "center", fontWeight: 700, textDecoration: "none" }}>Get Free Consultation</a>
        </div>
      )}
    </nav>
  );
}
// ── Hero ──────────────────────────────────────────────────────────────────────
function Hero() {
  const [counter, setCounter] = useState(0);
  const metrics = ["61% Drop-off Reduction", "4.9★ Avg Client Rating", "48h Discovery Kickoff", "200+ Products Designed"];

  useEffect(() => {
    const t = setInterval(() => setCounter(c => (c + 1) % metrics.length), 2400);
    return () => clearInterval(t);
  }, []);

  return (
    <section style={{
      paddingTop: 148, paddingBottom: 96,
      background: "linear-gradient(150deg,#1C0A12 0%,#4C0519 25%,#7F1D1D 55%,#1C1917 100%)",
      position: "relative", overflow: "hidden",
    }}>
      {/* Floating geometric shapes */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        {[...Array(14)].map((_, i) => (
          <div key={i} style={{
            position: "absolute",
            width: Math.random() * 120 + 40,
            height: Math.random() * 120 + 40,
            borderRadius: Math.random() > 0.5 ? "50%" : `${Math.random() * 40}%`,
            border: `1px solid rgba(244,63,94,${Math.random() * 0.15 + 0.04})`,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animation: `float ${Math.random() * 6 + 5}s ease-in-out infinite ${Math.random() * 3}s`,
            transform: `rotate(${Math.random() * 60}deg)`,
          }} />
        ))}
      </div>

      {/* Noise-grain overlay */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none", opacity: 0.04,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E")`,
        backgroundRepeat: "repeat", backgroundSize: "128px",
      }} />

      {/* Mesh gradient orbs */}
      <div style={{ position: "absolute", top: -200, right: -100, width: 800, height: 800, borderRadius: "50%", background: "radial-gradient(circle,rgba(244,63,94,.22) 0%,rgba(251,146,60,.08) 40%,transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: -150, left: -100, width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle,rgba(139,92,246,.15),transparent 65%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", top: "30%", left: "50%", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle,rgba(251,146,60,.08),transparent 70%)", pointerEvents: "none" }} />

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 40px", position: "relative" }}>
        {/* Breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 32, fontSize: 13, color: "rgba(255,255,255,.45)" }}>
          <Link to="/" style={{ color: "#FDA4AF", textDecoration: "none", fontWeight: 600 }}>Home</Link>
          <span>›</span><span>Services</span><span>›</span>
          <span style={{ color: "#fff", fontWeight: 600 }}>UI/UX Design</span>
        </div>

        <div className="uiux-hero-grid">
          {/* Left */}
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(244,63,94,.18)", border: "1px solid rgba(253,164,175,.35)", borderRadius: 99, padding: "6px 18px", marginBottom: 28 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#FDA4AF", display: "inline-block", animation: "pulse 2s infinite" }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: "#FDA4AF", letterSpacing: ".02em" }}>🎨 India's Top UI/UX Design Studio</span>
            </div>

            <h1 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(34px,4.5vw,60px)", fontWeight: 900, color: "#fff", lineHeight: 1.08, marginBottom: 22, letterSpacing: "-0.02em" }}>
              Design That Converts.<br />
              <span style={{ background: "linear-gradient(135deg,#FDA4AF 0%,#FB923C 50%,#FBBF24 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Experiences That Last.
              </span>
            </h1>
            <p style={{ fontSize: 17, color: "rgba(255,255,255,.65)", lineHeight: 1.85, maxWidth: 570, marginBottom: 36, fontWeight: 400 }}>
              We craft research-driven, pixel-perfect UI/UX for web, mobile, and SaaS — from UX strategy and wireframing to full design systems and developer-ready handoff that ships flawlessly.
            </p>

            {/* Rotating metric */}
            <div style={{ background: "rgba(244,63,94,.14)", border: "1px solid rgba(253,164,175,.28)", borderRadius: 14, padding: "14px 22px", marginBottom: 34, display: "inline-flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 18 }}>✨</span>
              <span style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 700, fontSize: 15, color: "#FDA4AF" }}>{metrics[counter]}</span>
            </div>

            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 44 }}>
              <a href="#contact" style={{
                background: "linear-gradient(135deg,#BE123C,#F43F5E,#FB923C)", color: "#fff",
                padding: "15px 32px", borderRadius: 12, fontWeight: 800, fontSize: 15,
                textDecoration: "none", boxShadow: "0 8px 28px rgba(244,63,94,.45)",
                transition: "transform .2s,box-shadow .2s", fontFamily: "'Plus Jakarta Sans',sans-serif",
                letterSpacing: "-.01em",
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 36px rgba(244,63,94,.55)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(244,63,94,.45)"; }}
              >🎨 Start Design Project</a>
              <a href="#case-studies" style={{
                background: "rgba(255,255,255,.07)", color: "#fff",
                padding: "15px 30px", borderRadius: 12, fontWeight: 700, fontSize: 15,
                textDecoration: "none", border: "1px solid rgba(255,255,255,.18)",
                transition: "all .2s", fontFamily: "'Plus Jakarta Sans',sans-serif",
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,.14)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.07)"; }}
              >🖼️ View Portfolio</a>
            </div>
            <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
              {[["200+", "Products Designed"], ["4.9★", "Client Rating"], ["61%", "Avg Conversion ↑"], ["48h", "Kickoff Turnaround"]].map(([v, l]) => (
                <div key={l}>
                  <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 900, fontSize: 22, color: "#FDA4AF", letterSpacing: "-0.02em" }}>{v}</div>
                  <div style={{ fontSize: 11.5, color: "rgba(255,255,255,.38)", fontWeight: 600, marginTop: 2 }}>{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Design Visual */}
          <div style={{ position: "relative", display: "flex", justifyContent: "center", alignItems: "center" }}>

            {/* Central design canvas */}
            <div style={{
              width: 270, height: 270, borderRadius: 40,
              background: "linear-gradient(135deg,rgba(244,63,94,.25),rgba(251,146,60,.15))",
              border: "1.5px solid rgba(253,164,175,.3)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 100, animation: "float 4s ease-in-out infinite",
              boxShadow: "0 0 100px rgba(244,63,94,.2), inset 0 1px 0 rgba(255,255,255,.1)",
              position: "relative", zIndex: 2,
              backdropFilter: "blur(12px)",
            }}>🎨</div>

            {/* Orbiting capability chips */}
            {[
              { label: "UX Research", angle: 0, color: "#FDA4AF" },
              { label: "Figma Design", angle: 60, color: "#FCA5A5" },
              { label: "Prototyping", angle: 120, color: "#FCD34D" },
              { label: "Design Systems", angle: 180, color: "#86EFAC" },
              { label: "Usability Tests", angle: 240, color: "#C4B5FD" },
              { label: "Dev Handoff", angle: 300, color: "#7DD3FC" },
            ].map((chip, i) => {
              const rad = (chip.angle * Math.PI) / 180;
              const r = 185;
              const x = Math.cos(rad) * r;
              const y = Math.sin(rad) * r;
              return (
                <div key={chip.label} style={{
                  position: "absolute",
                  left: `calc(50% + ${x}px)`, top: `calc(50% + ${y}px)`,
                  transform: "translate(-50%,-50%)",
                  background: "rgba(28,10,18,.88)",
                  border: `1px solid ${chip.color}55`,
                  borderRadius: 99,
                  padding: "8px 16px",
                  fontSize: 12, fontWeight: 700,
                  color: chip.color,
                  whiteSpace: "nowrap",
                  boxShadow: `0 4px 20px ${chip.color}20`,
                  zIndex: 3,
                  backdropFilter: "blur(8px)",
                  animation: `float ${3.5 + i * 0.3}s ease-in-out infinite ${i * 0.4}s`,
                }}>{chip.label}</div>
              );
            })}

            {/* Floating metric cards */}
            <div style={{ position: "absolute", top: -10, right: -20, background: "rgba(255,255,255,.96)", borderRadius: 18, padding: "14px 20px", boxShadow: "0 16px 48px rgba(244,63,94,.22)", border: "1.5px solid #FFE4E6", animation: "float 3s ease-in-out infinite", zIndex: 10, backdropFilter: "blur(8px)" }}>
              <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 900, fontSize: 20, color: "#BE123C", letterSpacing: "-0.02em" }}>4.9 ★</div>
              <div style={{ fontSize: 11, color: "#9CA3AF", fontWeight: 500, marginTop: 1 }}>Client Rating ⭐</div>
            </div>
            <div style={{ position: "absolute", bottom: 24, right: -16, background: "linear-gradient(135deg,#BE123C,#F43F5E)", borderRadius: 18, padding: "14px 20px", boxShadow: "0 12px 36px rgba(244,63,94,.4)", animation: "float 3.5s ease-in-out infinite .5s", zIndex: 10, textAlign: "center" }}>
              <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 900, fontSize: 20, color: "#fff", letterSpacing: "-0.02em" }}>61%</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.8)", fontWeight: 600 }}>Conversion ↑ 📈</div>
            </div>
            <div style={{ position: "absolute", bottom: 44, left: -12, background: "rgba(255,255,255,.96)", borderRadius: 16, padding: "12px 16px", boxShadow: "0 8px 28px rgba(0,0,0,.12)", border: "1.5px solid #FEF3C7", animation: "float 4s ease-in-out infinite 1s", zIndex: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 20 }}>🖼️</span>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 13, color: "#92400E", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>200+ Screens</div>
                  <div style={{ fontSize: 10, color: "#9CA3AF" }}>Avg design system</div>
                </div>
              </div>
            </div>

            {/* Extra decorative floating card */}
            <div style={{ position: "absolute", top: 50, left: -24, background: "rgba(255,255,255,.96)", borderRadius: 14, padding: "10px 14px", boxShadow: "0 6px 20px rgba(139,92,246,.18)", border: "1.5px solid #EDE9FE", animation: "float 4.5s ease-in-out infinite 1.5s", zIndex: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <span style={{ fontSize: 16 }}>♿</span>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 12, color: "#5B21B6", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>WCAG AA</div>
                  <div style={{ fontSize: 10, color: "#9CA3AF" }}>Accessible</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Stats Bar ─────────────────────────────────────────────────────────────────
function StatsBar() {
  return (
    <section style={{ background: "#0D0608", padding: "52px 40px", borderBottom: "1px solid rgba(244,63,94,.12)" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 32, textAlign: "center" }} className="uiux-stats-grid">
        {STATS.map(s => (
          <div key={s.lbl} style={{ position: "relative" }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>{s.icon}</div>
            <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(28px,3vw,42px)", fontWeight: 900, background: "linear-gradient(135deg,#FDA4AF,#FB923C)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text", lineHeight: 1, letterSpacing: "-0.02em" }}>{s.val}</div>
            <div style={{ color: "#6B7280", fontSize: 13, marginTop: 7, fontWeight: 500 }}>{s.lbl}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Services Grid ─────────────────────────────────────────────────────────────
function ServicesGrid() {
  const [hov, setHov] = useState(null);
  return (
    <section id="services" style={{ padding: "100px 40px", background: "#fff" }} className="uiux-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 68 }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: "#BE123C", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>What We Create</span>
          <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(28px,4vw,46px)", fontWeight: 900, color: "#0F172A", marginTop: 10, marginBottom: 16, letterSpacing: "-0.02em" }}>UI/UX Design Services</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 560, margin: "0 auto", lineHeight: 1.8 }}>
            From the first user interview to the final developer handoff — end-to-end design services that create products people love to use.
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {SERVICES.map((s, i) => (
            <div key={s.title}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 24, padding: "34px 30px",
                border: `2px solid ${hov === i ? s.color : "#F8FAFC"}`,
                boxShadow: hov === i ? `0 28px 70px ${s.color}1F` : "0 2px 16px rgba(0,0,0,.045)",
                transition: "all .32s cubic-bezier(.4,0,.2,1)",
                transform: hov === i ? "translateY(-8px)" : "none",
                position: "relative", overflow: "hidden",
              }}>
              {/* Subtle top accent */}
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${s.color},${s.color}60)`, transform: hov === i ? "scaleX(1)" : "scaleX(0)", transformOrigin: "left", transition: "transform .3s ease" }} />
              <div style={{ width: 58, height: 58, borderRadius: 16, background: `${s.color}12`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 22, border: `1.5px solid ${s.color}22` }}>
                {s.icon}
              </div>
              <h3 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 18, fontWeight: 800, color: "#0F172A", marginBottom: 10, letterSpacing: "-0.01em" }}>{s.title}</h3>
              <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.8, marginBottom: 20 }}>{s.desc}</p>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 18 }}>
                {s.tags.map(t => (
                  <span key={t} style={{ background: `${s.color}0F`, color: s.color, fontSize: 11, fontWeight: 700, padding: "4px 11px", borderRadius: 99, border: `1px solid ${s.color}22`, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>{t}</span>
                ))}
              </div>
              <div style={{ color: s.color, fontWeight: 700, fontSize: 13, opacity: hov === i ? 1 : 0, transition: "opacity .22s", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Explore Service →</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Use Cases by Industry ─────────────────────────────────────────────────────
function UseCases() {
  const [hov, setHov] = useState(null);
  return (
    <section style={{ padding: "100px 40px", background: "#FFFBFB" }} className="uiux-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 68 }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: "#BE123C", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Industries</span>
          <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(28px,4vw,46px)", fontWeight: 900, color: "#0F172A", marginTop: 10, marginBottom: 16, letterSpacing: "-0.02em" }}>Design for Every Industry</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.8 }}>Domain-specific design intuition — we know what users in your industry expect and how to exceed it.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {USE_CASES.map((uc, i) => (
            <div key={uc.industry}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 24, padding: "30px 26px",
                border: `2px solid ${hov === i ? uc.color : "#F1F5F9"}`,
                boxShadow: hov === i ? `0 20px 56px ${uc.color}16` : "0 2px 8px rgba(0,0,0,.04)",
                transition: "all .3s", transform: hov === i ? "translateY(-5px)" : "none",
              }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 22 }}>
                <div style={{ width: 54, height: 54, borderRadius: 16, background: `${uc.color}12`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, border: `1.5px solid ${uc.color}1E` }}>
                  {uc.icon}
                </div>
                <h3 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 800, fontSize: 18, color: "#0F172A", letterSpacing: "-0.01em" }}>{uc.industry}</h3>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {uc.examples.map(ex => (
                  <div key={ex} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: uc.color, flexShrink: 0, display: "inline-block" }} />
                    <span style={{ fontSize: 13.5, color: "#374151", fontWeight: 500 }}>{ex}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Tech Stack ────────────────────────────────────────────────────────────────
function TechStack() {
  const [active, setActive] = useState(0);
  const [hov, setHov] = useState(null);
  const cat = TECH_STACK[active];

  return (
    <section style={{ padding: "100px 40px", background: "#fff" }} className="uiux-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: "#BE123C", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Our Toolkit</span>
          <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(28px,4vw,46px)", fontWeight: 900, color: "#0F172A", marginTop: 10, marginBottom: 16, letterSpacing: "-0.02em" }}>Design Tools We Master</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.8 }}>The industry-leading tools powering every pixel of our UI/UX deliverables.</p>
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap", marginBottom: 44 }}>
          {TECH_STACK.map((t, i) => (
            <button key={t.category} onClick={() => { setActive(i); setHov(null); }} style={{
              padding: "10px 22px", borderRadius: 99, fontWeight: 700, fontSize: 13.5,
              cursor: "pointer", border: "2px solid", transition: "all .25s",
              fontFamily: "'Plus Jakarta Sans',sans-serif",
              background: active === i ? "linear-gradient(135deg,#BE123C,#F43F5E)" : "#fff",
              borderColor: active === i ? "#F43F5E" : "#E5E7EB",
              color: active === i ? "#fff" : "#6B7280",
              boxShadow: active === i ? "0 6px 20px rgba(244,63,94,.32)" : "none",
            }}>{t.category}</button>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: 18 }}>
          {cat.items.map((tech, i) => (
            <div key={tech.name}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
                padding: "32px 20px 24px", borderRadius: 22,
                border: `2px solid ${hov === i ? tech.color : "#F1F5F9"}`,
                background: hov === i ? `${tech.color}07` : "#FAFAFA",
                boxShadow: hov === i ? `0 14px 40px ${tech.color}24` : "0 1px 4px rgba(0,0,0,.04)",
                transition: "all .3s cubic-bezier(.4,0,.2,1)",
                transform: hov === i ? "translateY(-7px) scale(1.03)" : "none",
                cursor: "default", position: "relative", overflow: "hidden",
              }}>
              <div style={{ width: 72, height: 72, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 18, background: hov === i ? `${tech.color}14` : "#fff", border: `1px solid ${hov === i ? tech.color + "28" : "#E5E7EB"}`, boxShadow: hov === i ? `0 4px 18px ${tech.color}22` : "0 1px 4px rgba(0,0,0,.06)", transition: "all .3s" }}>
                <img src={tech.logo} alt={tech.name} style={{ width: 44, height: 44, objectFit: "contain", transition: "transform .3s", transform: hov === i ? "scale(1.12)" : "scale(1)" }}
                  onError={e => { e.target.style.display = "none"; e.target.parentNode.innerHTML = `<span style="font-size:22px;font-weight:900;color:${tech.color}">${tech.name[0]}</span>`; }}
                />
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: hov === i ? tech.color : "#1E293B", fontFamily: "'Plus Jakarta Sans',sans-serif", transition: "color .25s" }}>{tech.name}</div>
                {hov === i && <div style={{ fontSize: 10, color: tech.color, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", marginTop: 3 }}>Expert Level</div>}
              </div>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, borderRadius: "0 0 20px 20px", background: `linear-gradient(90deg,${tech.color},${tech.color}70)`, transform: hov === i ? "scaleX(1)" : "scaleX(0)", transformOrigin: "left", transition: "transform .32s" }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Process ───────────────────────────────────────────────────────────────────
function ProcessSection() {
  return (
    <section style={{ padding: "100px 40px", background: "linear-gradient(150deg,#1C0A12 0%,#4C0519 40%,#1C1917 100%)" }} className="uiux-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 76 }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: "#FDA4AF", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Our Process</span>
          <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(28px,4vw,46px)", fontWeight: 900, color: "#fff", marginTop: 10, marginBottom: 16, letterSpacing: "-0.02em" }}>From Blank Canvas to Launched Product</h2>
          <p style={{ color: "rgba(255,255,255,.55)", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.8 }}>A transparent, evidence-based 6-stage design methodology — where every decision is grounded in user data.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {PROCESS.map((step) => (
            <div key={step.n} style={{
              background: "rgba(255,255,255,.05)", borderRadius: 24, padding: "34px 30px",
              border: `1px solid rgba(255,255,255,.09)`,
              backdropFilter: "blur(12px)",
              position: "relative", overflow: "hidden",
              transition: "all .32s ease",
            }}
              onMouseEnter={e => { e.currentTarget.style.background = `rgba(244,63,94,.13)`; e.currentTarget.style.borderColor = `${step.color}55`; e.currentTarget.style.transform = "translateY(-4px)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.05)"; e.currentTarget.style.borderColor = "rgba(255,255,255,.09)"; e.currentTarget.style.transform = "none"; }}
            >
              <div style={{ position: "absolute", top: 22, right: 26, fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 64, fontWeight: 900, color: "rgba(255,255,255,.035)", lineHeight: 1, pointerEvents: "none", letterSpacing: "-0.04em" }}>{step.n}</div>
              <div style={{ width: 58, height: 58, borderRadius: 16, background: `${step.color}22`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 22, border: `1.5px solid ${step.color}38` }}>
                {step.icon}
              </div>
              <span style={{ fontSize: 11, fontWeight: 800, color: step.color, background: `${step.color}1A`, padding: "3px 12px", borderRadius: 99, display: "inline-block", marginBottom: 12, letterSpacing: ".04em", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>STEP {step.n}</span>
              <h3 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 800, fontSize: 18, color: "#fff", marginBottom: 10, letterSpacing: "-0.01em" }}>{step.title}</h3>
              <p style={{ color: "rgba(255,255,255,.55)", fontSize: 14, lineHeight: 1.8 }}>{step.desc}</p>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${step.color},${step.color}44)` }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Case Studies ──────────────────────────────────────────────────────────────
function CaseStudies() {
  const [hov, setHov] = useState(null);
  return (
    <section id="case-studies" style={{ padding: "100px 40px", background: "#fff" }} className="uiux-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 68 }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: "#BE123C", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Case Studies</span>
          <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(28px,4vw,46px)", fontWeight: 900, color: "#0F172A", marginTop: 10, marginBottom: 16, letterSpacing: "-0.02em" }}>Design That Moved the Needle</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.8 }}>Real numbers from real redesigns — not mockups, not concept art, shipped products in users' hands.</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 64 }}>
          {CASE_STUDIES.map((cs, idx) => (
            <div key={cs.title} style={{
              display: "grid", gridTemplateColumns: "1fr 1fr",
              gap: 56, alignItems: "center",
              direction: idx % 2 === 0 ? "ltr" : "rtl",
            }} className="uiux-cs-grid">
              <div style={{ direction: "ltr", position: "relative", borderRadius: 24, overflow: "hidden", boxShadow: `0 32px 80px ${cs.color}22` }}
                onMouseEnter={() => setHov(cs.title)}
                onMouseLeave={() => setHov(null)}
              >
                <img src={cs.img} alt={cs.title} style={{ width: "100%", height: 380, objectFit: "cover", display: "block", transition: "transform .5s ease", transform: hov === cs.title ? "scale(1.05)" : "scale(1)" }} />
                <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top, ${cs.color}44, transparent 60%)` }} />
                <div style={{ position: "absolute", top: 20, left: 20, background: cs.color, color: "#fff", fontSize: 12, fontWeight: 800, padding: "5px 16px", borderRadius: 99, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>{cs.industry}</div>
              </div>
              <div style={{ direction: "ltr" }}>
                <h3 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(24px,3vw,38px)", fontWeight: 900, color: "#0F172A", marginBottom: 14, letterSpacing: "-0.02em" }}>{cs.title}</h3>
                <p style={{ color: "#6B7280", fontSize: 16, lineHeight: 1.85, marginBottom: 26 }}>{cs.desc}</p>
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 30 }}>
                  {cs.tags.map(t => (
                    <span key={t} style={{ background: `${cs.color}0F`, color: cs.color, fontSize: 12, fontWeight: 700, padding: "5px 13px", borderRadius: 99, border: `1px solid ${cs.color}22`, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>{t}</span>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 32, marginBottom: 34, flexWrap: "wrap" }}>
                  {cs.metrics.map(m => (
                    <div key={m.lbl}>
                      <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 900, fontSize: 28, color: cs.color, lineHeight: 1, letterSpacing: "-0.03em" }}>{m.val}</div>
                      <div style={{ fontSize: 12, color: "#6B7280", fontWeight: 600, marginTop: 5 }}>{m.lbl}</div>
                    </div>
                  ))}
                </div>
                <a href="#" style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: cs.color, color: "#fff",
                  padding: "13px 28px", borderRadius: 12, fontWeight: 800, fontSize: 14,
                  textDecoration: "none", boxShadow: `0 6px 20px ${cs.color}32`,
                  transition: "transform .22s, box-shadow .22s", fontFamily: "'Plus Jakarta Sans',sans-serif",
                  letterSpacing: "-0.01em",
                }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 10px 28px ${cs.color}44`; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = `0 6px 20px ${cs.color}32`; }}
                >Read Full Case Study →</a>
              </div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 64 }}>
          <Link to="/portfolio" style={{
            background: "#fff", color: "#BE123C", border: "2px solid #FECDD3",
            padding: "14px 36px", borderRadius: 12, fontWeight: 800, fontSize: 15,
            textDecoration: "none", fontFamily: "'Plus Jakarta Sans',sans-serif", transition: "all .22s", display: "inline-block",
            letterSpacing: "-0.01em",
          }}
            onMouseEnter={e => { e.currentTarget.style.background = "#FFF1F2"; e.currentTarget.style.borderColor = "#FDA4AF"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#FECDD3"; }}
          >View All Design Projects →</Link>
        </div>
      </div>
    </section>
  );
}

// ── FAQ ───────────────────────────────────────────────────────────────────────
function FAQ() {
  const [open, setOpen] = useState(null);
  return (
    <section style={{ padding: "100px 40px", background: "#FFFBFB" }} className="uiux-section">
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: "#BE123C", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>FAQ</span>
          <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(28px,4vw,46px)", fontWeight: 900, color: "#0F172A", marginTop: 10, marginBottom: 16, letterSpacing: "-0.02em" }}>Frequently Asked Questions</h2>
          <p style={{ color: "#6B7280", fontSize: 16, lineHeight: 1.7 }}>Honest answers to the most common questions about UI/UX design projects.</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {FAQS.map((faq, i) => (
            <div key={i} style={{
              background: "#fff", borderRadius: 18,
              border: `2px solid ${open === i ? "#F43F5E" : "#FFE4E6"}`,
              overflow: "hidden", transition: "border-color .25s, box-shadow .25s",
              boxShadow: open === i ? "0 8px 36px rgba(244,63,94,.1)" : "0 1px 4px rgba(0,0,0,.04)",
            }}>
              <button onClick={() => setOpen(open === i ? null : i)} style={{
                width: "100%", padding: "22px 30px", background: "none", border: "none",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif",
              }}>
                <span style={{ fontWeight: 700, fontSize: 15.5, color: "#0F172A", textAlign: "left", lineHeight: 1.45, flex: 1, marginRight: 18, letterSpacing: "-0.01em" }}>{faq.q}</span>
                <div style={{
                  width: 34, height: 34, borderRadius: "50%",
                  background: open === i ? "linear-gradient(135deg,#BE123C,#F43F5E)" : "#FFF1F2",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .28s",
                }}>
                  <span style={{ color: open === i ? "#fff" : "#F43F5E", fontSize: 19, lineHeight: 1, display: "block", transform: open === i ? "rotate(45deg)" : "none", transition: "transform .28s" }}>+</span>
                </div>
              </button>
              {open === i && (
                <div style={{ padding: "0 30px 26px", animation: "fadeIn .22s ease" }}>
                  <p style={{ color: "#6B7280", fontSize: 14.5, lineHeight: 1.85, borderTop: "1px solid #FFF1F2", paddingTop: 18 }}>{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Contact ───────────────────────────────────────────────────────────────────
function ContactCTA() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", desc: "" });
  const [focused, setFocused] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const handleSubmit = e => { e.preventDefault(); setLoading(true); setTimeout(() => { setLoading(false); setSubmitted(true); }, 1600); };

  const iStyle = (n) => ({
    width: "100%", padding: "13px 16px", borderRadius: 12, fontSize: 14,
    border: `2px solid ${focused === n ? "#F43F5E" : "#FFE4E6"}`,
    background: focused === n ? "#FFF1F2" : "#FFFBFB",
    color: "#0F172A", outline: "none", fontFamily: "'Plus Jakarta Sans',sans-serif",
    transition: "all .22s", boxShadow: focused === n ? "0 0 0 4px rgba(244,63,94,.09)" : "none",
  });

  return (
    <section id="contact" style={{ padding: "100px 40px", background: "linear-gradient(150deg,#FFF5F5,#FFFBF0,#FFF1F2)" }} className="uiux-section">
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: "#BE123C", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Get Started</span>
          <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(28px,4vw,48px)", fontWeight: 900, color: "#0F172A", marginTop: 10, marginBottom: 14, letterSpacing: "-0.02em" }}>Start Your Design Project Today</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.8 }}>Tell us about your product and users — we'll deliver a free UX audit and design brief within 48 hours.</p>
        </div>
        <div className="uiux-contact-grid">
          {/* Info panel */}
          <div>
            <div style={{ background: "linear-gradient(135deg,#4C0519,#9F1239,#BE123C)", borderRadius: 28, padding: "40px 36px", marginBottom: 24, boxShadow: "0 24px 64px rgba(190,18,60,.28)", position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: -60, right: -60, width: 240, height: 240, borderRadius: "50%", background: "rgba(255,255,255,.05)" }} />
              <div style={{ position: "absolute", bottom: -40, left: -40, width: 180, height: 180, borderRadius: "50%", background: "rgba(251,146,60,.08)" }} />
              <h3 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 800, fontSize: 22, color: "#fff", marginBottom: 8, position: "relative", letterSpacing: "-0.02em" }}>Free UX Audit & Design Brief</h3>
              <p style={{ color: "rgba(255,255,255,.68)", fontSize: 14, lineHeight: 1.75, marginBottom: 32, position: "relative" }}>Share your product — we'll audit the UX, identify the top 3 improvement opportunities, and outline a design approach. No commitment required.</p>
              {[
                { icon: "📞", label: "Phone", val: "+91 96462 06032" },
                { icon: "✉️", label: "Email", val: "info@techvanto.com" },
                { icon: "📍", label: "Location", val: "New Delhi, India 🇮🇳" },
                { icon: "⏰", label: "Hours", val: "Mon–Sat, 9AM – 7PM IST" },
              ].map(item => (
                <div key={item.label} style={{ display: "flex", gap: 14, marginBottom: 18, alignItems: "flex-start", position: "relative" }}>
                  <div style={{ width: 40, height: 40, borderRadius: 12, background: "rgba(255,255,255,.14)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>{item.icon}</div>
                  <div>
                    <div style={{ fontSize: 10, color: "rgba(255,255,255,.45)", fontWeight: 800, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 2, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>{item.label}</div>
                    <div style={{ fontSize: 13.5, color: "#fff", fontWeight: 600 }}>{item.val}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ background: "#fff", borderRadius: 24, padding: "28px", border: "1.5px solid #FFE4E6", boxShadow: "0 4px 16px rgba(244,63,94,.07)" }}>
              <h4 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 800, fontSize: 15, color: "#0F172A", marginBottom: 18, letterSpacing: "-0.01em" }}>What You Get — Free</h4>
              {[
                ["🔍", "UX audit of your current product"],
                ["🎯", "Top 3 conversion improvement opportunities"],
                ["🎨", "Design direction recommendation"],
                ["🔒", "NDA signed before any discussion"],
                ["🗺️", "Project scope, timeline & investment estimate"],
              ].map(([icon, text]) => (
                <div key={text} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 13 }}>
                  <span style={{ fontSize: 16 }}>{icon}</span>
                  <span style={{ fontSize: 13.5, color: "#374151", fontWeight: 500 }}>{text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div style={{ background: "#fff", borderRadius: 28, padding: "46px", boxShadow: "0 8px 56px rgba(244,63,94,.08)", border: "1.5px solid #FFE4E6" }}>
            {submitted ? (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <div style={{ width: 92, height: 92, borderRadius: "50%", background: "linear-gradient(135deg,#BE123C,#F43F5E,#FB923C)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px", fontSize: 42, boxShadow: "0 14px 40px rgba(244,63,94,.35)" }}>🎨</div>
                <h3 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 24, fontWeight: 900, color: "#0F172A", marginBottom: 12, letterSpacing: "-0.02em" }}>Audit Requested! 🎉</h3>
                <p style={{ color: "#6B7280", fontSize: 15, lineHeight: 1.75, maxWidth: 320, margin: "0 auto 28px" }}>Our design team will review your product and deliver a free UX audit report within 48 hours.</p>
                <button onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", service: "", desc: "" }); }} style={{ background: "linear-gradient(135deg,#BE123C,#F43F5E)", color: "#fff", border: "none", padding: "13px 30px", borderRadius: 12, fontWeight: 800, fontSize: 14, cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Submit Another Request</button>
              </div>
            ) : (
              <>
                <h3 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 900, fontSize: 22, color: "#0F172A", marginBottom: 6, letterSpacing: "-0.02em" }}>Tell Us About Your Product</h3>
                <p style={{ color: "#9CA3AF", fontSize: 13.5, marginBottom: 32 }}>The more you share about your users and goals, the more specific our design audit will be.</p>
                <form onSubmit={handleSubmit}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }} className="uiux-form-row">
                    <div>
                      <label style={{ display: "block", fontSize: 11.5, fontWeight: 800, color: "#374151", marginBottom: 7, textTransform: "uppercase", letterSpacing: ".07em", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Full Name *</label>
                      <input name="name" value={form.name} onChange={handleChange} onFocus={() => setFocused("name")} onBlur={() => setFocused(null)} placeholder="Your name" required style={iStyle("name")} />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 11.5, fontWeight: 800, color: "#374151", marginBottom: 7, textTransform: "uppercase", letterSpacing: ".07em", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Work Email *</label>
                      <input type="email" name="email" value={form.email} onChange={handleChange} onFocus={() => setFocused("email")} onBlur={() => setFocused(null)} placeholder="name@company.com" required style={iStyle("email")} />
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }} className="uiux-form-row">
                    <div>
                      <label style={{ display: "block", fontSize: 11.5, fontWeight: 800, color: "#374151", marginBottom: 7, textTransform: "uppercase", letterSpacing: ".07em", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Phone *</label>
                      <input type="tel" name="phone" value={form.phone} onChange={handleChange} onFocus={() => setFocused("phone")} onBlur={() => setFocused(null)} placeholder="+91 XXXXX XXXXX" required style={iStyle("phone")} />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 11.5, fontWeight: 800, color: "#374151", marginBottom: 7, textTransform: "uppercase", letterSpacing: ".07em", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Design Service Needed</label>
                      <select name="service" value={form.service} onChange={handleChange} onFocus={() => setFocused("service")} onBlur={() => setFocused(null)} style={{ ...iStyle("service"), appearance: "none", cursor: "pointer" }}>
                        <option value="">Select service…</option>
                        {["UX Research & Audit", "Full Product Design", "Design System", "Mobile App UI", "SaaS Dashboard", "Usability Testing", "Accessibility Audit", "Design-to-Dev Handoff", "Not sure yet"].map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ marginBottom: 28 }}>
                    <label style={{ display: "block", fontSize: 11.5, fontWeight: 800, color: "#374151", marginBottom: 7, textTransform: "uppercase", letterSpacing: ".07em", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Product & User Description *</label>
                    <textarea name="desc" value={form.desc} onChange={handleChange} onFocus={() => setFocused("desc")} onBlur={() => setFocused(null)} placeholder="Describe your product, who your users are, what problems they face, and what a successful redesign would look like for your business." rows={5} required style={{ ...iStyle("desc"), resize: "vertical", minHeight: 130, lineHeight: 1.75 }} />
                  </div>
                  <p style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 20 }}>🔒 Your product details are completely confidential. We sign NDAs and never share your information.</p>
                  <button type="submit" disabled={loading} style={{
                    width: "100%", padding: "16px", borderRadius: 14,
                    background: loading ? "#FECDD3" : "linear-gradient(135deg,#BE123C,#F43F5E,#FB923C)",
                    color: "#fff", border: "none", cursor: loading ? "not-allowed" : "pointer",
                    fontWeight: 900, fontSize: 16, fontFamily: "'Plus Jakarta Sans',sans-serif",
                    boxShadow: loading ? "none" : "0 8px 28px rgba(244,63,94,.38)",
                    transition: "all .25s", display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                    letterSpacing: "-0.01em",
                  }}>
                    {loading ? "Submitting…" : "🎨 Get Free UX Audit"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function Footer() {
  const cols = [
    { title: "Services", links: ["Web Development", "Mobile Apps", "AI & ML", "Cloud & DevOps", "UI/UX Design", "E-Commerce"] },
    { title: "Company", links: ["About Us", "Portfolio", "Blog", "Careers", "Contact", "Privacy Policy"] },
    { title: "Technologies", links: ["React & Next.js", "Flutter & RN", "Node.js & Laravel", "AWS & GCP", "Docker & K8s", "Figma & XD"] },
  ];
  return (
    <footer style={{ background: "#0D0608", color: "#fff", padding: "76px 40px 32px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div className="footer-grid" style={{ marginBottom: 56 }}>
          <div>
            <div style={{ marginBottom: 16 }}>
              <img src={LOGO_SRC} alt="Techvanto" style={{ height: 140, width: "auto", objectFit: "contain", filter: "brightness(0) invert(1)", display: "block" }} />
            </div>
            <p style={{ color: "#94A3B8", lineHeight: 1.8, fontSize: 14, maxWidth: 300, marginBottom: 24 }}>
              Building world-class digital products for businesses across 20+ countries. Innovation. Scale. Excellence.
            </p>
            <div style={{ display: "flex", gap: 12 }}>
              {["𝕏", "in", "𝔾", "▶"].map((icon, i) => (
                <a key={i} href="#" style={{ width: 40, height: 40, borderRadius: 12, background: "rgba(255,255,255,.07)", display: "flex", alignItems: "center", justifyContent: "center", color: "#94A3B8", textDecoration: "none", fontSize: 14, fontWeight: 700, transition: "all .22s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#F43F5E"; e.currentTarget.style.color = "#fff"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.07)"; e.currentTarget.style.color = "#94A3B8"; }}
                >{icon}</a>
              ))}
            </div>
          </div>
          {cols.map(col => (
            <div key={col.title}>
              <h4 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 800, fontSize: 14, color: "#fff", marginBottom: 20, letterSpacing: ".03em" }}>{col.title}</h4>
              {col.links.map(l => (
                <a key={l} href="#" style={{ display: "block", color: "#64748B", fontSize: 13.5, textDecoration: "none", marginBottom: 10, transition: "color .15s", fontFamily: "'Plus Jakarta Sans',sans-serif" }}
                  onMouseEnter={e => e.target.style.color = "#FDA4AF"}
                  onMouseLeave={e => e.target.style.color = "#64748B"}
                >{l}</a>
              ))}
            </div>
          ))}
        </div>
        <div style={{ borderTop: "1px solid rgba(244,63,94,.12)", paddingTop: 28, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <p style={{ color: "#475569", fontSize: 13, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>© 2025 Techvanto Private Limited. All rights reserved.</p>
          <p style={{ color: "#475569", fontSize: 13, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Made with ❤️ in India 🇮🇳</p>
        </div>
      </div>
    </footer>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function UIUXDesign() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { font-family: 'Plus Jakarta Sans', -apple-system, sans-serif; background: #fff; color: #0F172A; overflow-x: hidden; }

        @keyframes fadeIn { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:none; } }
        @keyframes float  { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-12px); } }
        @keyframes pulse  { 0%,100% { opacity:1; } 50% { opacity:.35; } }

        .nav-links        { display:flex; align-items:center; gap:4px; }
        .nav-cta          { display:flex !important; }
        .mob-menu-btn     { display:none; }

        .uiux-hero-grid    { display:grid; grid-template-columns:1fr 1fr; gap:64px; align-items:center; }
        .uiux-stats-grid   { display:grid; grid-template-columns:repeat(4,1fr); gap:32px; }
        .uiux-cs-grid      { display:grid; grid-template-columns:1fr 1fr; gap:56px; align-items:center; }
        .uiux-contact-grid { display:grid; grid-template-columns:1fr 1.55fr; gap:48px; align-items:start; }
        .footer-grid       { display:grid; grid-template-columns:2fr 1fr 1fr 1fr; gap:48px; }
        .uiux-form-row     { display:grid; grid-template-columns:1fr 1fr; gap:16px; }

        @media (max-width:1100px) {
          .uiux-hero-grid  { gap:40px; }
          .footer-grid     { grid-template-columns:1fr 1fr; gap:32px; }
          .uiux-stats-grid { grid-template-columns:repeat(2,1fr); }
        }
        @media (max-width:900px) {
          .uiux-hero-grid    { grid-template-columns:1fr; gap:48px; }
          .uiux-cs-grid      { grid-template-columns:1fr !important; gap:36px !important; direction:ltr !important; }
          .uiux-contact-grid { grid-template-columns:1fr; gap:32px; }
          .footer-grid       { grid-template-columns:1fr 1fr; gap:32px; }
          .nav-links         { display:none !important; }
          .nav-cta           { display:none !important; }
          .mob-menu-btn      { display:flex !important; }
        }
        @media (max-width:640px) {
          .footer-grid     { grid-template-columns:1fr; }
          .uiux-stats-grid { grid-template-columns:repeat(2,1fr); gap:24px; }
          .uiux-form-row   { grid-template-columns:1fr !important; }
        }
        @media (max-width:500px) { .topbar-right { display:none !important; } }
        @media (max-width:900px)  { .uiux-section { padding-left:20px !important; padding-right:20px !important; } }
        @media (max-width:640px)  { .uiux-section { padding-left:16px !important; padding-right:16px !important; padding-top:64px !important; padding-bottom:64px !important; } }
      `}</style>

      <Hero />
      <StatsBar />
      <ServicesGrid />
      <UseCases />
      <TechStack />
      <ProcessSection />
      <CaseStudies />
      <FAQ />
    </>
  );
}